import React from "react";
import * as Animate from "react-reveal";
import { Link } from "react-router-dom";
import HeaderNavigation from "../../../components/layout/HeaderNavigation";
import HeroImage from "../../../assets/images/png/global-mba-graduation-group-v1.webp";
import { ArrowUpRight, ArrowRight } from "../../../components/svgs";
import MBACohortStrip from "./MBACohortStrip";

const BROCHURE_URL =
  "https://drive.google.com/file/d/1gcNMbCEL30s3g8Y9vZaMbfkBDntihi7e/view?usp=sharing";
const ADVISOR_WHATSAPP = "https://wa.me/2348035152982";

const snapshotItems = [
  { label: "Duration", value: "5 months" },
  { label: "Format", value: "Live online" },
  { label: "Schedule", value: "Weekly sessions" },
  { label: "Learning style", value: "Practical and case-based" },
  { label: "University network", value: "5 global partners" },
];

const MBAHeaderSection = () => {
  return (
    <header className="w-full relative bg-white" id="top">
      <HeaderNavigation bgdark />

      <MBACohortStrip />

      <section className="container mx-auto px-4 py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Animate.Fade bottom>
            <div>
              <p className="flex items-center gap-3 font-poppins text-primary text-sm font-extrabold tracking-widest uppercase mb-5">
                <span className="w-8 h-0.5 bg-secondary inline-block flex-shrink-0"></span>
                Global business leadership, growth & AI
              </p>

              <p className="font-poppins text-secondary text-base font-extrabold mb-3">
                Utiva Global MBA
              </p>

              <h1 className="font-poppins text-primary font-bold text-4xl md:text-5xl xl:text-7xl leading-tight tracking-tight mb-6">
                One Global MBA.{" "}
                <span className="text-default">
                  Five international university partnerships.
                </span>
              </h1>

              <p className="font-poppins text-gray text-base leading-relaxed max-w-lg mb-8">
                A practical five-month program for professionals ready to build
                stronger judgement across leadership, growth, finance,
                execution, AI and digital transformation—with international
                learning access through Utiva and five global university
                partners.
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-9">
                <Link
                  to="/mba/apply"
                  className="text-white bg-primary custom-btn custom-btn-with-shadow-primary px-7 py-4 text-center rounded-xl inline-flex items-center gap-3 font-poppins font-bold text-sm focus:outline-none"
                >
                  Apply to the Global MBA{" "}
                  <span aria-hidden="true">
                    <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
                  </span>
                </Link>
                <a
                  href={BROCHURE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-poppins text-primary text-sm font-bold hover:underline underline-offset-4"
                >
                  Download brochure <span aria-hidden="true">↓</span>
                </a>
              </div>

              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-hire-tint-blue text-primary font-poppins font-extrabold text-sm grid place-items-center flex-shrink-0">
                  UT
                </span>
                <p className="font-poppins text-gray text-sm leading-snug m-0">
                  <strong className="text-default">Not sure if it fits?</strong>
                  <br />
                  <a
                    href={ADVISOR_WHATSAPP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold"
                  >
                    Speak with a program advisor{" "}
                    <ArrowRight fill="currentColor" className="inline-block" />
                  </a>
                </p>
              </div>
            </div>
          </Animate.Fade>

          <Animate.Fade right>
            <figure className="relative m-0">
              <span
                className="absolute -top-4 -right-4 w-24 h-24 bg-secondary rounded-3xl -z-10"
                aria-hidden="true"
              ></span>
              <img
                src={HeroImage}
                alt="Illustrative group of African professionals wearing Utiva Global MBA graduation sashes"
                fetchpriority="high"
                className="w-full h-hero-image object-cover object-top rounded-3xl shadow-course-feature-lg"
              />
              <figcaption className="absolute left-0 right-6 sm:right-auto bottom-6 sm:-left-8 sm:min-w-[290px] bg-primary text-white rounded-2xl border border-white/30 shadow-2xl grid grid-cols-3 gap-0 p-5">
                <span className="col-span-3 text-secondary text-xs font-poppins font-extrabold tracking-widest uppercase mb-3">
                  Global program at a glance
                </span>
                <div className="grid gap-0.5">
                  <strong className="font-poppins text-sm">5 months</strong>
                  <small className="font-poppins text-blue-100 text-xs">
                    Live online
                  </small>
                </div>
                <div className="grid gap-0.5 border-l border-white/20 pl-3">
                  <strong className="font-poppins text-sm">5 partners</strong>
                  <small className="font-poppins text-blue-100 text-xs">
                    Global universities
                  </small>
                </div>
                <div className="grid gap-0.5 border-l border-white/20 pl-3">
                  <strong className="font-poppins text-sm">
                    1 certificate
                  </strong>
                  <small className="font-poppins text-blue-100 text-xs">
                    Utiva Global MBA
                  </small>
                </div>
              </figcaption>
            </figure>
          </Animate.Fade>
        </div>
      </section>

      <section
        className="bg-hire-tint-blue border-y border-hire-line"
        aria-label="Program format"
      >
        <div className="container mx-auto px-4 py-7 grid grid-cols-2 md:grid-cols-5">
          {snapshotItems.map((item, i) => (
            <div
              key={item.label}
              className={`flex items-center gap-3 px-4 py-2 md:py-0 ${
                i > 0 ? "md:border-l border-hire-line" : ""
              }`}
            >
              <span className="font-poppins text-secondary font-extrabold text-sm flex-shrink-0">
                0{i + 1}
              </span>
              <p className="font-poppins text-gray text-sm m-0 grid">
                {item.label}
                <strong className="text-primary mt-0.5">{item.value}</strong>
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-primary text-white">
        <div className="container mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-8">
          <Animate.Fade bottom>
            <div>
              <p className="flex items-center gap-3 font-poppins text-secondary text-sm font-extrabold tracking-widest uppercase mb-2">
                <span className="w-8 h-0.5 bg-secondary inline-block flex-shrink-0"></span>
                Your next level starts here
              </p>
              <h2 className="font-poppins text-white font-bold text-xl md:text-2xl tracking-tight mb-1">
                Build the business confidence to lead across borders.
              </h2>
              <p className="font-poppins text-blue-100 text-sm m-0">
                Applications lead to the official Utiva Global MBA application
                page.
              </p>
            </div>
          </Animate.Fade>
          <Link
            to="/mba/apply"
            className="text-primary bg-secondary custom-btn px-7 py-4 text-center rounded-xl inline-flex items-center gap-3 font-poppins font-bold text-sm focus:outline-none flex-shrink-0"
          >
            Start your application{" "}
            <span aria-hidden="true">
              <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
            </span>
          </Link>
        </div>
      </section>
    </header>
  );
};

export default MBAHeaderSection;
